import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { can as roleCan, type Action } from "@sp/shared/roles";
import type { WorkspaceView } from "../lib/api/types";
import { prefs } from "../lib/storage";
import { useMe } from "./session";

const KEY = "sp.workspace";

interface WorkspaceValue {
  workspace: WorkspaceView;
  workspaces: WorkspaceView[];
  select: (id: string) => void;
  can: (action: Action) => boolean;
  /** Writes need a plan; stop and reads work without one. */
  writable: boolean;
}

/** Makes `id` the workspace shown next, e.g. right after accepting an invitation to it. */
export const rememberWorkspace = (id: string) => prefs.set(KEY, id);

const WorkspaceContext = createContext<WorkspaceValue | null>(null);

/** Picks the workspace: ?workspace= (billing returns with it), then the last one used, then the personal one. */
function initialId(workspaces: WorkspaceView[]): string | undefined {
  const fromUrl = new URLSearchParams(location.search).get("workspace");
  const ids = new Set(workspaces.map((w) => w.id));
  for (const id of [fromUrl, prefs.get(KEY)]) if (id && ids.has(id)) return id;
  return (workspaces.find((w) => w.isPersonal) ?? workspaces[0])?.id;
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const me = useMe();
  const [selected, setSelected] = useState(() => initialId(me.workspaces));
  const workspace = me.workspaces.find((w) => w.id === selected) ?? me.workspaces.find((w) => w.isPersonal) ?? me.workspaces[0];

  const select = useCallback((id: string) => { rememberWorkspace(id); setSelected(id); }, []);

  const value = useMemo<WorkspaceValue | null>(() => workspace ? {
    workspace,
    workspaces: me.workspaces,
    select,
    can: (a) => roleCan(workspace.role, a),
    writable: workspace.access.access === "full",
  } : null, [workspace, me.workspaces, select]);

  if (!value) return <p className="p-8">Your account has no workspace yet. Reload the page in a moment.</p>;
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): WorkspaceValue {
  const v = useContext(WorkspaceContext);
  if (!v) throw new Error("useWorkspace outside WorkspaceProvider");
  return v;
}
